import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLms } from '../../context/LmsContext';
import { Card, PageHeader, StatusBadge } from '../../components/ui';
import { byId, getCourse, getLearningNavigation } from '../../utils/lmsSelectors';

function toGoogleDrivePreviewUrl(url) {
  if (!url) return '';
  if (!url.includes('drive.google.com')) return '';
  if (url.includes('/preview')) return url;

  const fileMatch = url.match(/\/file\/d\/([^/]+)/);
  if (fileMatch?.[1]) return `https://drive.google.com/file/d/${fileMatch[1]}/preview`;

  try {
    const parsed = new URL(url);
    const id = parsed.searchParams.get('id');
    if (id) return `https://drive.google.com/file/d/${id}/preview`;
  } catch (error) {
    return '';
  }

  return '';
}

function toEmbeddedResourceUrl(resource) {
  const url = resource?.url || '';
  if (!url) return '';

  const drivePreview = toGoogleDrivePreviewUrl(url);
  if (drivePreview) return drivePreview;

  if (url.includes('docs.google.com/document/') || url.includes('docs.google.com/spreadsheets/') || url.includes('docs.google.com/presentation/')) {
    return url.replace(/\/edit.*$/, '/preview').replace(/\/view.*$/, '/preview');
  }

  return url;
}

function firstLessonResource(lesson) {
  if (!lesson) return null;
  if (lesson.lessonType === 'video') {
    const videoResource = (lesson.resources || []).find((resource) => resource.resourceType === 'google_drive_video');
    return videoResource || (lesson.videoUrl ? {
      id: `${lesson.id}-video-resource`,
      title: 'Google Drive training video',
      resourceType: 'google_drive_video',
      url: lesson.videoUrl
    } : null);
  }
  return (lesson.resources || []).find((resource) => resource.resourceType !== 'google_drive_video' && resource.resourceType !== 'practical_session') || null;
}

function resourceTypeLabel(type) {
  const labels = {
    google_drive_video: 'Google Drive video',
    sop_link: 'SOP / internal resource',
    drive_link: 'SOP / internal resource',
    doc: 'Google Doc',
    sheet: 'Google Sheet',
    pdf: 'PDF',
    template: 'Template',
    exercise_file: 'Exercise file / Google Drive folder',
    external_url: 'External URL'
  };
  return labels[type] || 'Lesson resource';
}

function PrimaryLessonAction({ assignment, nav, isCompleted, isWaitingReview, needsSubmission, submissionReady, saving, onComplete, isPractical }) {
  if (isWaitingReview) {
    return <button className="button button-secondary" disabled>Waiting for trainer review</button>;
  }

  if (isPractical && !isCompleted) {
    return <button className="button button-secondary" disabled>Attendance pending trainer confirmation</button>;
  }

  if (!isCompleted) {
    return (
      <button className="button button-primary" disabled={saving || (needsSubmission && !submissionReady)} onClick={onComplete}>
        {saving ? 'Saving...' : needsSubmission ? 'Submit for review' : nav.next ? 'Complete lesson and continue' : 'Complete lesson and finish course'}
      </button>
    );
  }

  return nav.next ? (
    <Link className="button button-primary" to={nav.next.path}>Next: {nav.next.title}</Link>
  ) : (
    <Link className="button button-primary" to={`/learner/course/${assignment.id}`}>Course summary</Link>
  );
}

export default function LessonPage() {
  const { lessonProgressId } = useParams();
  const [assignmentId, lessonId] = lessonProgressId.split(':');
  const { data, currentUser, completeLesson } = useLms();
  const navigate = useNavigate();
  const [submissionText, setSubmissionText] = useState('');
  const [submissionUrl, setSubmissionUrl] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const assignment = data.assignments.find((item) => item.id === assignmentId && item.assignedToUserId === currentUser.id);
  const lesson = byId(data.lessons, lessonId);

  const lessonResource = useMemo(() => firstLessonResource(lesson), [lesson]);
  const embeddedResourceUrl = useMemo(() => toEmbeddedResourceUrl(lessonResource), [lessonResource]);
  const hasExternalResource = Boolean(lessonResource?.url);

  if (!assignment || !lesson) return <Card>Lesson not found or access removed.</Card>;

  const course = getCourse(data, assignment.courseId);
  const progress = assignment.lessonProgress?.[lesson.id] || { status: 'not_started' };
  const nav = getLearningNavigation(data, assignment, 'lesson', lesson.id);
  const isExercise = lesson.lessonType === 'exercise';
  const isPractical = lesson.lessonType === 'practical';
  const needsSubmission = !isPractical && (lesson.completionType === 'trainer_approval' || isExercise);
  const isCompleted = progress.status === 'completed';
  const isWaitingReview = progress.status === 'needs_review';
  const completedExerciseUrl = submissionUrl.trim();
  const submissionReady = needsSubmission ? (isExercise ? completedExerciseUrl.includes('drive.google.com') : submissionText.trim().length > 5 || completedExerciseUrl.length > 5) : true;

  const complete = async () => {
    if (saving) return;
    setMessage('');
    setSaving(true);
    const response = await completeLesson(assignment.id, lesson.id, { submissionText, submissionUrl: completedExerciseUrl }, currentUser.id);
    setSaving(false);
    if (!response.ok) {
      setMessage(response.message || 'Lesson progress could not be saved.');
      return;
    }
    if (needsSubmission) {
      navigate(`/learner/course/${assignment.id}`);
      return;
    }
    navigate(nav.next?.path || `/learner/course/${assignment.id}`);
  };

  return (
    <>
      <PageHeader
        title={lesson.title}
        eyebrow={course?.title}
        description={`${lesson.lessonType} lesson • ${lesson.estimatedMinutes} minutes`}
        action={<Link className="button button-secondary" to={`/learner/course/${assignment.id}`}>Back to course</Link>}
      />

      <Card className="lesson-page-card">
        <div className="builder-topline"><StatusBadge status={progress.status} /><span>{lesson.lessonType}</span></div>

        {hasExternalResource && isExercise ? (
          <div className="resource-viewer-card exercise-resource-card">
            <div className="resource-viewer-header">
              <div>
                <h2>{lessonResource.title || 'Exercise file'}</h2>
                <p className="muted small">Open or download the trainer-provided Google Drive exercise file/folder, complete the work, then submit your completed Google Drive link below.</p>
              </div>
              <a className="button button-primary" href={lessonResource.url} target="_blank" rel="noreferrer">Open/download exercise file</a>
            </div>
          </div>
        ) : hasExternalResource ? (
          <div className="resource-viewer-card">
            <div className="resource-viewer-header">
              <div>
                <h2>{lessonResource.title || 'Lesson Resource'}</h2>
                <p className="muted small">{resourceTypeLabel(lessonResource.resourceType)} opens inside the LMS. Access still depends on Google Drive/company sharing permissions.</p>
              </div>
              <a className="button button-ghost" href={lessonResource.url} target="_blank" rel="noreferrer">Open in new tab</a>
            </div>
            <div className={lesson.lessonType === 'video' ? 'enphase-video-shell' : 'document-frame'}>
              {embeddedResourceUrl ? (
                <iframe title={`${lesson.title} lesson resource`} src={embeddedResourceUrl} allow="autoplay; fullscreen" allowFullScreen />
              ) : (
                <div className="resource-fallback">
                  <p>This resource cannot be embedded by the browser.</p>
                  <a href={lessonResource.url} target="_blank" rel="noreferrer">Open resource</a>
                </div>
              )}
            </div>
          </div>
        ) : isPractical ? (
          <div className="resource-viewer-card practical-session-card">
            <div className="resource-viewer-header">
              <div>
                <h2>Practical Activity</h2>
                <p className="muted small">Completion is confirmed by the trainer after attendance is verified.</p>
              </div>
              {lesson.meetingLink && <a className="button button-primary" href={lesson.meetingLink} target="_blank" rel="noreferrer">Join meeting</a>}
            </div>
            <div className="practical-session-details">
              <div><span className="muted small">Session Date</span><strong>{lesson.sessionDate ? new Date(lesson.sessionDate).toLocaleString() : 'To be announced'}</strong></div>
              <div><span className="muted small">Meeting Link</span><strong>{lesson.meetingLink ? 'Available' : 'To be added later'}</strong></div>
            </div>
          </div>
        ) : (
          <div className="resource-viewer-card">
            <div className="resource-viewer-header">
              <div>
                <h2>Lesson Content</h2>
                <p className="muted small">Review the lesson content below.</p>
              </div>
            </div>
            <div className="lesson-content"><p>{lesson.contentHtml}</p></div>
          </div>
        )}

        {lesson.contentHtml && hasExternalResource && (
          <div className="lesson-content"><h3>Lesson notes</h3><p>{lesson.contentHtml}</p></div>
        )}

        {needsSubmission && !isCompleted && !isWaitingReview && (
          <div className="submission-panel">
            <h3>{isExercise ? 'Exercise submission' : 'Practical activity submission'}</h3>
            <p className="muted small">{isExercise ? 'Paste the Google Drive link for your completed exercise. Make sure the trainer can access the file/folder.' : 'Submit a Drive link, note, or summary for trainer approval.'}</p>
            {isExercise && (
              <label className="form-row">
                <span>Completed exercise Google Drive link</span>
                <input type="url" placeholder="https://drive.google.com/..." value={submissionUrl} onChange={(e) => setSubmissionUrl(e.target.value)} />
              </label>
            )}
            <label className="form-row">
              <span>{isExercise ? 'Notes for trainer (optional)' : 'Submission notes or link'}</span>
              <textarea rows="6" placeholder={isExercise ? 'Add notes, assumptions, or items for review...' : 'Paste Drive link or write review notes...'} value={submissionText} onChange={(e) => setSubmissionText(e.target.value)} />
            </label>
          </div>
        )}

        {isWaitingReview && progress.submissionUrl && <p className="feedback-box">Submitted exercise: <a href={progress.submissionUrl} target="_blank" rel="noreferrer">Open learner Drive link</a></p>}
        {progress.feedback && <p className="feedback-box">Trainer feedback: {progress.feedback}</p>}

        <div className="learner-nav-row lesson-action-row">
          {nav.previous ? (
            <Link className="button button-ghost" to={nav.previous.path}>Previous: {nav.previous.title}</Link>
          ) : (
            <Link className="button button-ghost" to={`/learner/course/${assignment.id}`}>Back to course outline</Link>
          )}
          <PrimaryLessonAction
            assignment={assignment}
            nav={nav}
            isCompleted={isCompleted}
            isWaitingReview={isWaitingReview}
            needsSubmission={needsSubmission}
            submissionReady={submissionReady}
            saving={saving}
            onComplete={complete}
            isPractical={isPractical}
          />
        </div>

        {message && <p className="form-error">{message}</p>}
        {needsSubmission && !submissionReady && !isCompleted && !isWaitingReview && <p className="small muted">{isExercise ? 'Paste a Google Drive link to your completed exercise before submitting for review.' : 'Add your submission note or resource link before submitting for review.'}</p>}
      </Card>
    </>
  );
}
